// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — the DOM screens: menu, help, about, and the two results panels. Pure HTML builders (no
// state), wired up by main.ts. The score is the same currency in every mode (higher is better):
// chains + clear bonuses. A full clear is the "perfect", so results show stones cleared out of the
// cairn's size — what you left standing.

import { cairnLabel, escapeHtml, scoreLabel } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function bestText(best: number | null): string {
  return best === null || best < 0 ? 'No run yet' : `Best: ${scoreLabel(best)}`;
}

function modeChips(modeId: string): string {
  return MODE_IDS.map(
    (id) =>
      `<button class="chip${id === modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeId}">${escapeHtml(modeOf(id).name)}</button>`,
  ).join('');
}

export function menuHtml(o: { modeId: string; best: number | null; muted: boolean }): string {
  const mode = modeOf(o.modeId);
  return `
    <section class="screen menu">
      <div class="brand">
        <div class="logo" aria-hidden="true"></div>
        <h1>Cairn</h1>
        <p class="tagline">Take a stone one rune from your hand and chain the whole cairn down.</p>
      </div>
      <div class="modepick">
        <div class="chips" role="group" aria-label="Choose a mode">${modeChips(o.modeId)}</div>
        <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
        <p class="mode-best">${bestText(o.best)}</p>
      </div>
      <div class="menu-actions">
        <button class="btn primary" id="playRandom">Play</button>
        <button class="btn" id="playDaily">Daily cairn</button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <h2>How to play</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <ul class="help-list">
      <li><strong>Tap a stone</strong> whose rune is <strong>one step above or below</strong> your hand stone — <strong>aces wrap to kings</strong>. It becomes your new hand and uncovers the stones beneath it.</li>
      <li>A stone you can take now <strong>lifts and glows amber</strong>; a covered stone is shadowed.</li>
      <li>Each take in a row builds your <strong>heat</strong> multiplier. <strong>Tap the scree</strong> (or press <strong>D</strong>) to draw a fresh stone — it resets heat and spends one draw.</li>
      <li>Clear the whole cairn to win.</li>
      <li><strong>Cascade:</strong> a stream of small cairns against a clock — bank as many as you can.</li>
      <li><strong>Scree:</strong> a hard draw budget — run out with stones left and you are buried.</li>
      <li>Keyboard: arrows/Tab move the cursor, Enter takes, D draws.</li>
    </ul>
    <button class="btn primary sheet-close">Got it</button>`;
}

export function aboutHtml(): string {
  return `
    <h2>About Cairn</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <p>Dismantle a cairn — a pyramid of runed stones — by chaining stones one rune apart. Every cairn
       is built from a seed and is guaranteed to have a full-clear solution, so a friend on the same
       link plays the identical deal — race them for the highest score.</p>
    <p><strong>Multiplayer</strong> is peer-to-peer over WebRTC — there is no game server. Connecting to a friend
       uses a public signaling relay only to introduce the two browsers; no game data is stored on a server.</p>
    <p>No accounts, no cookies, no tracking. ${ATTRIB}</p>
    <button class="btn primary sheet-close">Close</button>`;
}

function medal(rank: number): string {
  return ['🥇', '🥈', '🥉'][rank] ?? `${rank + 1}.`;
}

function outcomeLabel(r: PeerResult, mode: Mode): string {
  if (r.left) return 'left';
  if (mode.stream) return `${scoreLabel(r.score)} · ${cairnLabel(r.cairns)}`;
  const chain = r.bestChain > 1 ? ` · best ×${r.bestChain}` : '';
  if (r.done && r.won) return `${scoreLabel(r.score)} · cleared${chain}`;
  if (r.done) return `${scoreLabel(r.score)} · ${r.cleared} cleared${chain}`;
  return 'playing…';
}

/** Solo results. */
export function soloResultsHtml(o: {
  mode: Mode;
  reason: string | null;
  won: boolean;
  score: number;
  cleared: number;
  total: number;
  cairns: number;
  bestChain: number;
  best: number | null;
  daily: boolean;
  isNewBest: boolean;
}): string {
  let sub: string;
  if (o.mode.stream) sub = o.isNewBest ? 'New best!' : `Banked ${cairnLabel(o.cairns)}`;
  else if (o.won) sub = o.isNewBest ? 'Cairn cleared — new best!' : 'Cairn cleared';
  else if (o.reason === 'buried') sub = `Buried — ${o.cleared}/${o.total} stones cleared`;
  else sub = `Out of moves — ${o.cleared}/${o.total} stones cleared`;
  const chain = o.bestChain > 1 ? `Longest chain ×${o.bestChain}` : '';
  return `
    <section class="screen results">
      <p class="res-mode">${escapeHtml(o.mode.name)}${o.daily ? ' · Daily' : ''}</p>
      <h2 class="res-time ${o.won || o.mode.stream ? 'win' : 'dnf'}">${escapeHtml(scoreLabel(o.score))}</h2>
      <p class="res-sub">${escapeHtml(sub)}</p>
      <p class="res-falls">${escapeHtml(chain)}${chain ? ' · ' : ''}${escapeHtml(bestText(o.best))}</p>
    </section>`;
}

/** Multiplayer results: EVERY player's outcome (principle #9), plus the match tally. */
export function multiResultsHtml(ranked: PeerResult[], selfSeat: number | null, mode: Mode, tally: number[]): string {
  const rows = ranked
    .map((r, i) => {
      const me = r.seat === selfSeat;
      const top = i === 0 && !r.left && r.score > 0;
      return `<li class="res-row${me ? ' me' : ''}${r.left ? ' gone' : ''}">
        <span class="res-rank">${top ? medal(i) : '·'}</span>
        <span class="res-name">${escapeHtml(r.name)}${me ? ' <em>(you)</em>' : ''}</span>
        <span class="res-out">${escapeHtml(outcomeLabel(r, mode))}</span>
        <span class="res-wins">${tally[r.seat] ? '★'.repeat(Math.min(tally[r.seat], 5)) : ''}</span>
      </li>`;
    })
    .join('');
  return `
    <p class="res-mode">${escapeHtml(mode.name)} · most points wins</p>
    <ul class="res-list">${rows}</ul>`;
}
